// rich-html-sanitizer.ts — Nettoyeur HTML allowlist IN-HOUSE côté CLIENT (0 dépendance).
//
// POURQUOI un sanitizer CÔTÉ CLIENT (défense en profondeur, R24-WYSIWYG) : l'éditeur est passé
// d'une <textarea> (source inerte) à une zone `contentEditable` VIVANTE dans le DOM de l'admin.
// Un contenu collé (paste) ou produit par `document.execCommand` atterrit donc dans le DOM PARENT :
// sans nettoyage, un `<img onerror>` collé s'exécuterait chez l'admin (self-XSS). Ce module
// neutralise ce vecteur AVANT insertion/émission. Il ne REMPLACE PAS le nettoyage autoritaire
// serveur (`@jeko/shared-kernel` `sanitizeCguContentHtml`, choke point de write) : c'est une
// SECONDE couche, alignée sur la MÊME politique d'allowlist. Le serveur reste la référence.
//
// POSTURE : DEFAULT-DENY / REJECT-UNKNOWN. On ne « strippe » pas des motifs d'un texte de confiance ;
// on RECONSTRUIT la sortie de zéro à partir d'une allowlist stricte, en pur traitement de chaîne
// (aucune dépendance, exécutable en jsdom). Tout ce qui n'est pas explicitement permis est retiré,
// le texte est ré-échappé, seul `href` http(s) survit comme attribut.
//
// PARITÉ D'ALLOWLIST avec le serveur : identique + `u` (soulignement) + tables simples
// (`table/thead/tbody/tr/th/td`), car DBU §2.7/§2.9 l.2032 liste EXPLICITEMENT « soulignement » et
// « tables simples » parmi les fonctions d'édition. Le serveur (`@jeko/shared-kernel`) est étendu en
// miroir pour que ces balises SURVIVENT au write (round-trip), sans quoi elles seraient perdues.
//
// ALIAS SÉMANTIQUES : `execCommand('bold'|'italic')` produit `<b>`/`<i>` selon le navigateur. On les
// normalise en `<strong>`/`<em>` À L'ÉMISSION pour que la sortie soit conforme à l'allowlist serveur
// (qui n'autorise que `strong`/`em`) — sinon le gras/italique serait strippé au write.

/** Alias de balises normalisées vers leur forme sémantique canonique (allowlist). */
const TAG_ALIASES: ReadonlyMap<string, string> = new Map([
  ['b', 'strong'],
  ['i', 'em'],
]);

/** Balises autorisées (allowlist stricte et fermée). RIEN d'autre ne survit. */
const ALLOWED_TAGS: ReadonlySet<string> = new Set([
  'h1',
  'h2',
  'h3',
  'p',
  'ul',
  'ol',
  'li',
  'strong',
  'em',
  'u',
  'a',
  'br',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
]);

/** Balises vides (auto-fermantes, sans contenu ni fermeture). */
const VOID_TAGS: ReadonlySet<string> = new Set(['br']);

/**
 * Balises DANGEREUSES mais VOID (`meta`/`link`/`base`) : DROP du marqueur seul, JAMAIS de saut de
 * contenu (elles n'ont pas de fermeture). BUGFIX 2026-08-20 (parité serveur) : un collage CTRL+V dont
 * le presse-papiers HTML commence par `<meta charset=…>` (fragment navigateur) voyait tout son corps
 * supprimé car `skipDangerousContent('meta')` cherchait un `</meta>` inexistant → EOF. CTRL+SHIFT+V
 * (texte brut, sans meta) fonctionnait, d'où le symptôme. Balises toujours interdites, juste inertes.
 */
const VOID_DANGEROUS_TAGS: ReadonlySet<string> = new Set(['meta', 'link', 'base']);

/**
 * Balises à CONTENU dangereux (raw-text / embarqué exécutable) : on retire la balise ET tout son
 * contenu jusqu'à la fermeture correspondante. Retirer seulement les marqueurs laisserait fuir le
 * corps (ex. `<style>` → CSS injecté, `<script>` → JS en clair). Default-deny sur le sous-arbre.
 */
const DANGEROUS_CONTENT_TAGS: ReadonlySet<string> = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'noscript',
  'template',
  'svg',
  'math',
  'title',
  'textarea',
  'xmp',
  'frame',
  'frameset',
  'noframes',
  'link',
  'meta',
  'base',
]);

/** Borne de taille de l'entrée (garde DoS / structure aberrante). ~200 Ko couvrent largement. */
const MAX_INPUT_LENGTH = 200_000;

/** Vrai si le code de caractère est un espace ou un caractère de contrôle (<= 0x20) ou U+00A0. */
function isWhitespaceOrControl(code: number): boolean {
  return code <= 0x20 || code === 0x00a0;
}

/** Vrai si le caractère peut composer un nom de balise (lettre ou chiffre ASCII). */
function isNameChar(ch: string): boolean {
  return (
    (ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z') || (ch >= '0' && ch <= '9')
  );
}

/** Normalise un nom de balise (alias sémantique : b→strong, i→em ; sinon inchangé). */
function normalizeTagName(name: string): string {
  return TAG_ALIASES.get(name) ?? name;
}

/**
 * Échappe un nœud TEXTE. Préserve les entités déjà valides (`&amp;`, `&#233;`, `&eacute;`) pour ne
 * pas double-encoder, mais échappe tout `&` isolé, ainsi que `<`/`>` résiduels.
 */
function escapeText(text: string): string {
  return text
    .replace(/&(?!(?:#\d+;|#x[0-9a-fA-F]+;|[a-zA-Z][a-zA-Z0-9]*;))/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Échappe une valeur d'attribut destinée à des guillemets doubles (`href`). */
function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Retire tous les espaces et caractères de contrôle d'une chaîne (par code de caractère). */
function stripWhitespaceAndControl(value: string): string {
  let out = '';
  for (const ch of value) {
    if (!isWhitespaceOrControl(ch.codePointAt(0) ?? 0)) {
      out += ch;
    }
  }
  return out;
}

/** `String.fromCodePoint` tolérant (code invalide → chaîne vide). */
function safeFromCodePoint(code: number): string {
  if (!Number.isFinite(code) || code < 0 || code > 0x10ffff) {
    return '';
  }
  try {
    return String.fromCodePoint(code);
  } catch {
    return '';
  }
}

/**
 * Décode les entités susceptibles d'obfusquer un schéma d'URL, pour la SEULE vérification de schéma.
 * On NE réémet JAMAIS cette forme décodée ; elle ne sert qu'au contrôle.
 */
function decodeForSchemeCheck(value: string): string {
  return value
    .replace(/&#x([0-9a-fA-F]+);?/g, (_m, hex: string) => safeFromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);?/g, (_m, dec: string) => safeFromCodePoint(parseInt(dec, 10)))
    .replace(/&colon;/gi, ':')
    .replace(/&tab;/gi, '\t')
    .replace(/&newline;/gi, '\n')
    .replace(/&NewLine;/g, '\n');
}

/**
 * Valide l'`href` d'un `<a>` : autorise UNIQUEMENT `http:` / `https:`. Rejette `javascript:`, `data:`,
 * `vbscript:`, `file:`, les schémas obfusqués. @returns la valeur à émettre (échappée) si sûre, sinon
 * `null` (l'attribut est retiré → `<a>` inerte).
 */
function safeHref(rawValue: string | undefined): string | null {
  if (rawValue === undefined) {
    return null;
  }
  const normalized = stripWhitespaceAndControl(decodeForSchemeCheck(rawValue)).toLowerCase();
  if (!normalized.startsWith('http://') && !normalized.startsWith('https://')) {
    return null;
  }
  return escapeAttr(rawValue.trim());
}

interface ParsedTag {
  readonly name: string;
  readonly closing: boolean;
  readonly selfClosing: boolean;
  readonly attrs: ReadonlyMap<string, string>;
  /** Index juste APRÈS la balise dans l'entrée. */
  readonly next: number;
}

/**
 * Analyse une balise commençant à `start` (`input[start] === '<'`). @returns le token, ou `null`
 * si `<` n'introduit pas un nom de balise valide (à traiter alors comme un `<` littéral en texte).
 */
function parseTag(input: string, start: number): ParsedTag | null {
  const n = input.length;
  let i = start + 1;
  let closing = false;
  if (input[i] === '/') {
    closing = true;
    i += 1;
  }
  const nameStart = i;
  while (i < n && isNameChar(input[i] ?? '')) {
    i += 1;
  }
  if (i === nameStart) {
    return null;
  }
  const name = input.slice(nameStart, i).toLowerCase();
  const attrs = new Map<string, string>();
  let selfClosing = false;

  while (i < n) {
    while (i < n && isWhitespaceOrControl(input.charCodeAt(i))) {
      i += 1;
    }
    if (i >= n) {
      return { name, closing, selfClosing, attrs, next: n };
    }
    const ch = input[i];
    if (ch === '>') {
      i += 1;
      break;
    }
    if (ch === '/') {
      if (input[i + 1] === '>') {
        selfClosing = true;
        i += 2;
        break;
      }
      i += 1;
      continue;
    }
    const anStart = i;
    while (
      i < n &&
      !isWhitespaceOrControl(input.charCodeAt(i)) &&
      input[i] !== '=' &&
      input[i] !== '>' &&
      input[i] !== '/'
    ) {
      i += 1;
    }
    const attrName = input.slice(anStart, i).toLowerCase();
    let attrValue = '';
    while (i < n && isWhitespaceOrControl(input.charCodeAt(i))) {
      i += 1;
    }
    if (input[i] === '=') {
      i += 1;
      while (i < n && isWhitespaceOrControl(input.charCodeAt(i))) {
        i += 1;
      }
      const quote = input[i];
      if (quote === '"' || quote === "'") {
        i += 1;
        const vStart = i;
        while (i < n && input[i] !== quote) {
          i += 1;
        }
        attrValue = input.slice(vStart, i);
        i += 1;
      } else {
        const vStart = i;
        while (i < n && !isWhitespaceOrControl(input.charCodeAt(i)) && input[i] !== '>') {
          i += 1;
        }
        attrValue = input.slice(vStart, i);
      }
    }
    if (attrName.length > 0) {
      attrs.set(attrName, attrValue);
    }
  }

  return { name, closing, selfClosing, attrs, next: i };
}

/**
 * Saute le contenu d'une balise à contenu dangereux jusqu'à sa fermeture `</name>` (insensible à
 * la casse). @returns l'index juste après la fermeture, ou la fin de l'entrée si non fermée.
 */
function skipDangerousContent(input: string, from: number, name: string): number {
  const lower = input.toLowerCase();
  const closeToken = `</${name}`;
  const idx = lower.indexOf(closeToken, from);
  if (idx === -1) {
    return input.length;
  }
  const gt = input.indexOf('>', idx);
  return gt === -1 ? input.length : gt + 1;
}

/**
 * Nettoie un HTML riche selon l'allowlist stricte (reconstruction default-deny). @returns un HTML
 * sûr ne contenant QUE des balises autorisées (+ `href` http(s) validé), équilibré. Au-delà de la
 * borne de taille, l'entrée est TRONQUÉE (côté client : jamais d'exception qui casserait la frappe ;
 * le serveur, lui, rejette en 400 — c'est la borne autoritaire).
 */
export function sanitizeRichHtml(input: string): string {
  const source = input.length > MAX_INPUT_LENGTH ? input.slice(0, MAX_INPUT_LENGTH) : input;
  const n = source.length;
  const out: string[] = [];
  const stack: string[] = [];
  let i = 0;

  while (i < n) {
    const ch = source[i];
    if (ch === '<') {
      if (source.startsWith('<!--', i)) {
        const end = source.indexOf('-->', i + 4);
        i = end === -1 ? n : end + 3;
        continue;
      }
      if (source.startsWith('<!', i) || source.startsWith('<?', i)) {
        const end = source.indexOf('>', i);
        i = end === -1 ? n : end + 1;
        continue;
      }
      const tag = parseTag(source, i);
      if (tag === null) {
        out.push('&lt;');
        i += 1;
        continue;
      }
      i = tag.next;
      const name = normalizeTagName(tag.name);

      if (DANGEROUS_CONTENT_TAGS.has(tag.name)) {
        // EXCEPTION void (meta/link/base) : pas de fermeture → on ne saute PAS de contenu (sinon on
        // consomme tout le reste, cf. VOID_DANGEROUS_TAGS). Marqueur retiré, suite gardée.
        if (
          !tag.closing &&
          !tag.selfClosing &&
          !VOID_TAGS.has(tag.name) &&
          !VOID_DANGEROUS_TAGS.has(tag.name)
        ) {
          i = skipDangerousContent(source, i, tag.name);
        }
        continue;
      }

      if (!ALLOWED_TAGS.has(name)) {
        // Balise hors allowlist (div, span, img, font…) : marqueurs retirés, texte adjacent gardé.
        continue;
      }

      if (VOID_TAGS.has(name)) {
        out.push(`<${name} />`);
        continue;
      }

      if (tag.closing) {
        const idx = stack.lastIndexOf(name);
        if (idx === -1) {
          continue;
        }
        for (let k = stack.length - 1; k >= idx; k -= 1) {
          out.push(`</${stack[k]}>`);
        }
        stack.length = idx;
        continue;
      }

      if (name === 'a') {
        const href = safeHref(tag.attrs.get('href'));
        out.push(href === null ? '<a>' : `<a href="${href}">`);
      } else {
        out.push(`<${name}>`);
      }
      stack.push(name);
      if (tag.selfClosing) {
        out.push(`</${name}>`);
        stack.pop();
      }
      continue;
    }

    let j = i;
    while (j < n && source[j] !== '<') {
      j += 1;
    }
    out.push(escapeText(source.slice(i, j)));
    i = j;
  }

  for (let k = stack.length - 1; k >= 0; k -= 1) {
    out.push(`</${stack[k]}>`);
  }

  return out.join('');
}
